import type { InternalAxiosRequestConfig } from 'axios'
import type { AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import axios from 'axios'
import Cookies from 'js-cookie'
import { supabase } from '@/lib/supabase'

export const baseURL = import.meta.env.VITE_API_URL

export const PAYMENT_REQUIRED_EVENT = 'payment-required-event'

export const ORG_MODULE_ACCESS_DENIED_ERROR_CODE = 'ORG_MODULE_ACCESS_DENIED' as const

export const ORG_MODULE_ACCESS_DENIED_EVENT = 'org-module-access-denied-event'

type RequestConfigWithRetry = InternalAxiosRequestConfig & { _retry?: boolean }

export type PaymentRequiredError = {
  paymentRequired?: boolean
  response?: { status?: number }
}

export function isPaymentRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const candidate = error as PaymentRequiredError
  return candidate.paymentRequired === true || candidate.response?.status === 402
}

let refreshPromise: Promise<string | null> | null = null

async function refreshSession(): Promise<string | null> {
  if (refreshPromise) return refreshPromise
  refreshPromise = supabase.auth.refreshSession().then(({ data, error }) => {
    if (!error && data?.session?.access_token) return data.session.access_token
    return null
  }).finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  const { auth } = useAuthStore.getState()
  if (auth.accessToken) {
    config.headers.Authorization = auth.accessToken
  }
  return config
}, (error) => {
  return Promise.reject(error)
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 404) {
      const url = error.config?.url || '';
      const silentEndpoints = [
        '/ai-hub/assistant/',
        '/ai-hub/chats',
        '/conversations',
        '/ai-hub/analytics/',
        '/chat-history'
      ];

      if (silentEndpoints.some(endpoint => url.includes(endpoint))) {
        return Promise.reject({
          ...error,
          silentError: true,
          response: {
            ...error.response,
            data: error.response?.data || {
              message: 'Endpoint not found',
              data: null
            }
          }
        });
      }
    }

    if (error.response && error.response.status === 402) {
      const paymentRequiredEvent = new CustomEvent(PAYMENT_REQUIRED_EVENT);
      window.dispatchEvent(paymentRequiredEvent);

      (error as AxiosError & { paymentRequired?: boolean }).paymentRequired = true;
      return Promise.reject(error);
    }

    const status = error.response?.status
    const config = error.config as RequestConfigWithRetry | undefined
    const isAuthError = status === 401 || status === 403

    if (isAuthError && config && !config._retry) {
      const newToken = await refreshSession()
      if (newToken) {
        useAuthStore.getState().auth.setAccessToken(newToken)
        config._retry = true
        config.headers.Authorization = newToken
        return apiClient(config)
      }
    }

    if (status === 401) {
      Cookies.remove('workstation_access_token', { path: '/' });
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('auth-token')) {
          localStorage.removeItem(key);
        }
      });
      window.location.href = "/sign-in";
    }

    if (
      status === 403 &&
      error.response?.data?.errorCode === ORG_MODULE_ACCESS_DENIED_ERROR_CODE
    ) {
      window.dispatchEvent(new CustomEvent(ORG_MODULE_ACCESS_DENIED_EVENT))
      ;(error as AxiosError & { orgModuleAccessDenied?: boolean }).orgModuleAccessDenied = true
      return Promise.reject(error)
    }

    return Promise.reject(error);
  }
);

export default apiClient